type PaystackResponse = { reference: string; status?: string; trans?: string };

type PaystackSetupOptions = {
  key: string;
  email: string;
  amount: number;
  currency: string;
  ref: string;
  metadata?: { custom_fields: { display_name: string; variable_name: string; value: string }[] };
  callback: (response: PaystackResponse) => void;
  onClose: () => void;
};

declare global {
  interface Window {
    PaystackPop?: { setup: (options: PaystackSetupOptions) => { openIframe: () => void } };
  }
}

const SCRIPT_SRC = "https://js.paystack.co/v1/inline.js";
let loader: Promise<void> | null = null;

/** Loads Paystack Inline only when a visitor starts a payment. */
export function loadPaystack(): Promise<void> {
  if (window.PaystackPop) return Promise.resolve();
  if (loader) return loader;
  loader = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      loader = null;
      reject(new Error("Paystack could not be loaded"));
    };
    document.body.appendChild(script);
  });
  return loader;
}

export function createReference(prefix = "MCC-FORM") {
  return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
}

type PayOptions = {
  publicKey: string;
  email: string;
  amountKobo: number;
  currency: string;
  fields: Record<string, string>;
};

/** Opens the Paystack popup. Resolves with the reference on success, rejects if closed or failed. */
export async function payWithPaystack({ publicKey, email, amountKobo, currency, fields }: PayOptions): Promise<string> {
  await loadPaystack();
  const pop = window.PaystackPop;
  if (!pop) throw new Error("Paystack unavailable");

  return new Promise((resolve, reject) => {
    const handler = pop.setup({
      key: publicKey,
      email,
      amount: amountKobo,
      currency,
      ref: createReference(),
      metadata: {
        custom_fields: Object.entries(fields).map(([k, v]) => ({ display_name: k, variable_name: k.toLowerCase().replace(/\s+/g, "_"), value: v })),
      },
      callback: (response) => resolve(response.reference),
      onClose: () => reject(new Error("closed")),
    });
    handler.openIframe();
  });
}
